import React, { ReactNode, useEffect, useState } from 'react';

import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import round from 'lodash/round';
import { customTheme } from 'theme';

import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { CountFontSize } from 'containers/Dashboard/types';
import {
  DashboardOverviewTooltip,
  DashboardOverviewTooltipProps,
} from 'containers/Dashboard/utils/OverviewDashboardTooltip';
import {
  renderCloudIcons,
  renderPercentChange,
} from 'containers/Dashboard/utils/utils';

interface Props {
  content?: ReactNode;
  label?: string;
  labelTooltip?: ReactNode;
  icon?: ReactNode;
  tooltip?: DashboardOverviewTooltipProps;
  cloudProviders?: string[];
  count?: number;
  showPercentChange?: boolean;

  onClick?();
  footer?: {
    isLoading?: boolean;
    content: ReactNode;
  };

  change?: {
    first?: number;
    last?: number;
    totalChange?: number;
    percentChange?: number | string;
    isLoading: boolean;
    changeColor?: string;
  };
  isLoading?: boolean;
  info?: {
    message: string | ReactNode;
    isLoading?: boolean;
    onClick?();
    enabled?: boolean;
    bg?: string;
  };
}

export const SummaryCard: React.FC<Props> = props => {
  const {
    label,
    labelTooltip,
    icon,
    isLoading,
    count,
    info,
    change,
    content,
    cloudProviders,
    tooltip,
    onClick,
    showPercentChange = true,
    footer,
  } = props;

  const [percentChange, setPercentChange] = useState(0);
  const [totalChange, setTotalChange] = useState(0);

  useEffect(() => {
    if (!!change) {
      const val =
        change?.totalChange ?? (change?.last ?? 0) - (change?.first ?? 0);
      setTotalChange(val);
      if (val === 0) setPercentChange(0);
      else
        setPercentChange(
          !!change?.percentChange && change?.percentChange !== '-'
            ? round(change?.percentChange as number, 1)
            : !!change?.first
            ? round((val / change?.first) * 100, 1)
            : 100,
        );
    }
  }, [change]);

  const renderTag = () => (
    <Box
      px={3}
      color={customTheme.colors.gray['300']}
      bg={info?.bg ?? (info?.enabled ? '#ebf5de' : '#f7dddd')}
      onClick={e => {
        e?.stopPropagation();
        info?.onClick?.();
      }}
      fontSize="15px"
      borderRadius={3}
    >
      <Center>{info?.message}</Center>
    </Box>
  );

  const renderContent = () => (
    <Stack
      w="full"
      h="full"
      justify="space-between"
      onClick={onClick}
      cursor={!!onClick ? 'pointer' : 'default'}
    >
      <Stack>
        {/* Keep label on one line; cloud icons take the remaining width and
            collapse into a +N tag (see CloudIcons) instead of wrapping */}
        <HStack w="full" justify="space-between">
          <HStack flexShrink={0}>
            {icon && (
              <Box boxSize={6} color="primary">
                <Center>{icon}</Center>
              </Box>
            )}
            <Box fontSize={16} whiteSpace="nowrap">
              {label}
            </Box>
          </HStack>
          <Box flex={1} minW={0}>
            {/* Summary cards keep the smaller size-4 icons (default is 5). */}
            {renderCloudIcons(cloudProviders, { iconSize: 4 })}
          </Box>
        </HStack>
      </Stack>
      <HStack w="full" justify="space-between" spacing={0}>
        <Box fontSize={CountFontSize}>
          {isLoading ? <CustomSpinner /> : formatNumber(count ?? 0, 1)}
        </Box>
        {info &&
          (info?.isLoading ? (
            <Box w={32}>
              <CustomSpinner />
            </Box>
          ) : tooltip && !info?.enabled ? (
            <DashboardOverviewTooltip {...tooltip} button={renderTag()} />
          ) : (
            renderTag()
          ))}
      </HStack>
      {!!change && (
        <HStack w="full" justify="space-between" fontSize="15px">
          <Box>
            {showPercentChange &&
              (change?.isLoading ? (
                <CustomSpinner />
              ) : (
                renderPercentChange(percentChange, change?.changeColor)
              ))}
          </Box>
          <Box
            color={
              totalChange === 0
                ? customTheme.colors.gray['300']
                : change?.changeColor ??
                  (totalChange < 0
                    ? customTheme.colors.green['300']
                    : 'critical')
            }
          >
            {(totalChange < 0 ? '' : '+') + formatNumber(totalChange)} in last 7
            days
          </Box>
        </HStack>
      )}
      {!!footer && (
        <HStack
          w="full"
          justify="space-between"
          fontSize="15px"
          color={customTheme.colors.gray['300']}
        >
          {footer?.isLoading && (
            <Box>
              <CustomSpinner />
            </Box>
          )}
          {footer?.content}
        </HStack>
      )}
    </Stack>
  );

  return (
    <Box py={4} px={label ? 4 : 2} w="full" h="full">
      {content ? (
        <Center h="full">{content}</Center>
      ) : labelTooltip ? (
        <CustomTooltip label={labelTooltip} styles={{ text: { h: 'full' } }}>
          <Box w="full" h="full">
            {renderContent()}
          </Box>
        </CustomTooltip>
      ) : (
        renderContent()
      )}
    </Box>
  );
};
